import PropTypes from "prop-types";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useState, useMemo, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";

/**
 * PriceHistogram component that displays a bar chart of price distribution.
 * Handles both desktop (onClick) and mobile (onTouchStart) interactions.
 * @param {Object} props - Component props.
 * @param {Array} props.priceBuckets - Array of price bucket objects with minPrice, maxPrice, and count.
 * @param {number} props.effectiveMaxPrice - Maximum effective price for outlier detection.
 * @param {Function} props.onBarClick - Callback function called when a bar is clicked/touched.
 * @param {Function} [props.currencyConverter] - Function to convert price values for display.
 * @returns {JSX.Element|null} The PriceHistogram component or null if no price buckets.
 */
const PriceHistogram = ({ priceBuckets, effectiveMaxPrice, onBarClick, currencyConverter = (v) => Math.round(v) }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const touchHandledRef = useRef(false);
  const { selectedCurrency, isMobile } = useSelector(
    (state) => ({
      selectedCurrency: state?.currencyList?.userSelectedCurrency,
      isMobile: state?.appConfig?.isMobile,
    }),
    shallowEqual,
  );

  // Build chart data as categorical buckets (one bar per bucket)
  const chartData = useMemo(() => {
    return (priceBuckets || []).map((bucket, index) => {
      const isOutlier = !!bucket.isOutlier || (effectiveMaxPrice && bucket.minPrice >= effectiveMaxPrice);
      // plotValue uses log transform to reduce skew, but tooltip will show raw count
      const plotValue = Math.log((bucket.count || 0) + 1); // mild transform
      return {
        key: `${index}`,
        label: `${bucket.minPrice}-${bucket.maxPrice}`,
        count: Number(bucket.count || 0),
        minPrice: bucket.minPrice,
        maxPrice: bucket.maxPrice,
        plotValue,
        isOutlier,
        index,
      };
    });
  }, [priceBuckets, effectiveMaxPrice]);

  if (!priceBuckets || priceBuckets.length === 0) return null;

  /**
   * Custom tooltip component for the price histogram chart.
   * @param {Object} props - Tooltip props from Recharts.
   * @param {boolean} props.active - Whether the tooltip is active.
   * @param {Array} props.payload - Array of data payload for the tooltip.
   * @returns {JSX.Element|null} The tooltip component or null if not active.
   */
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="border-grey-500 z-50 whitespace-nowrap rounded border bg-grey-100 px-3 py-2 shadow-2xl">
          <p className="text-12 font-semibold text-gray-900">
            {`${selectedCurrency?.currency?.currencyCode || ""} ${currencyConverter(
              data?.minPrice,
            )} - ${selectedCurrency?.currency?.currencyCode || ""} ${currencyConverter(data?.maxPrice)}`}
          </p>
          <p className="mt-1 text-12 text-gray-600">{`${data?.count} products`}</p>
        </div>
      );
    }
    return null;
  };

  /* eslint-disable react/boolean-prop-naming */
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
      PropTypes.shape({
        payload: PropTypes.shape({
          minPrice: PropTypes.number,
          maxPrice: PropTypes.number,
          count: PropTypes.number,
        }),
      }),
    ),
  };

  /**
   * Handle mouse enter on bar
   */
  const onHandleBarMouseEnter = (_, index) => {
    setHoveredIndex(index);
  };

  /**
   * Handle mouse leave on bar
   */
  const onHandleBarMouseLeave = () => {
    setHoveredIndex(null);
  };

  /**
   * Handle click on bar (for desktop)
   */
  const onHandleBarClick = (event) => {
    // Skip if touch event already handled this (mobile)
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    // Recharts provides activeTooltipIndex for click events sometimes,
    // fall back to activeIndex or payload index
    const idx = Number(event?.activeTooltipIndex ?? event?.activeIndex ?? event?.payload?.index ?? NaN);
    if (Number.isFinite(idx) && chartData[idx] && onBarClick) {
      const { minPrice, maxPrice } = chartData[idx];
      const convertedMin = currencyConverter(minPrice);
      const convertedMax = currencyConverter(maxPrice);
      onBarClick([convertedMin, convertedMax]);
    }
  };

  /**
   * Handle touch on wrapper (for mobile)
   * Calculates which bar was touched based on touch position
   */
  const onHandleWrapperTouch = (event) => {
    if (!isMobile || !onBarClick || !event.target) return;

    // Prevent click event from firing after touch
    touchHandledRef.current = true;
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);

    // Find the bar element that was touched
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (!touch) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const barWidth = rect.width / chartData.length;
    const idx = Math.floor(x / barWidth);

    if (idx >= 0 && idx < chartData.length && chartData[idx]) {
      const { minPrice, maxPrice } = chartData[idx];
      const convertedMin = currencyConverter(minPrice);
      const convertedMax = currencyConverter(maxPrice);
      onBarClick([convertedMin, convertedMax]);
    }
  };

  // NOTE: ResponsiveContainer needs parent with explicit height (you had 40px).
  // If the chart looks squashed increase height (e.g. 60-80).
  return (
    <div className="w-full" style={{ height: "60px" }} {...(isMobile ? { onTouchStart: onHandleWrapperTouch } : {})}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 6, right: 0, left: 0, bottom: 6 }}
          barCategoryGap={0}
          {...(!isMobile ? { onClick: (event) => onHandleBarClick(event) } : {})}
        >
          {/* Use categorical X axis (hide ticks) */}
          <XAxis dataKey="label" type="category" hide={true} />
          <Tooltip content={<CustomTooltip />} position={{ y: -40 }} />
          <Bar
            dataKey="plotValue" // plot transformed value for visual height
            radius={[2, 2, 2, 2]}
            stroke="none"
            onMouseEnter={onHandleBarMouseEnter}
            onMouseLeave={onHandleBarMouseLeave}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={hoveredIndex === index ? "#737530" : "#D1D5DB"}
                style={{ cursor: "pointer", transition: "fill 0.2s" }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

PriceHistogram.propTypes = {
  priceBuckets: PropTypes.array.isRequired,
  effectiveMaxPrice: PropTypes.number,
  onBarClick: PropTypes.func,
  currencyConverter: PropTypes.func,
};

export default PriceHistogram;
